import { getDefaultSubTab, canAccessSubTab, SECTIONS } from '../../features/workspace/utils/rolePermissions';

/**
 * Vart en notis leder när man klickar på den.
 *
 * Ersätter switch-satsen som låg i NotificationIcon. Med 58 notistyper blev
 * den ohållbar, och 42 av dem föll till samma default — flikarna "kanban",
 * som UE inte har och projektör saknar helt.
 *
 * Notiser djuplänkar till rätt SIDA. Mail gör tvärtom och går alltid till
 * inloggningen, eftersom mottagaren där nästan alltid är utloggad.
 *
 * Att lägga till en ny notistyp = en rad i TABELL nedan.
 */

const KUND = '/myproject';
const WS = '/workspace';

// ── Sökvägsbyggare ─────────────────────────────────────────────────────────
// Alla tar emot samma kontext och returnerar en sökväg eller null.
// null betyder "ingen vettig länk" och gör notisen oklickbar i stället för
// att skicka användaren någonstans slumpmässigt.

const kundSida = (flik) => ({ projectId }) => (projectId ? `${KUND}/${projectId}/${flik}` : null);
const projektering = (flik) => ({ projectId }) => (projectId ? `${WS}/projektering/${projectId}/${flik}` : null);
/**
 * Möten och ÄTA. BK, IA och admin har dem i Projektering, byggaren i
 * Produktion — samma flik, olika sektion. Rollen avgör vilken.
 */
const motenEllerAta = (flik, query = () => '') => (ctx) => {
  if (!ctx.projectId) return null;
  const sektion = canAccessSubTab(ctx.role, SECTIONS.PROJEKTERING, flik) ? 'projektering' : 'produktion';
  return `${WS}/${sektion}/${ctx.projectId}/${flik}${query(ctx)}`;
};
// Protokollet har ingen egen vy — notisen öppnar det på mötet i Möten-fliken.
const protokollPaMotet = motenEllerAta('moten', ({ metadata }) =>
  (metadata.meetingId ? `?protokoll=${metadata.meetingId}` : ''));
const fast = (path) => () => path;
/**
 * Kalkylen för en upphandling.
 *
 * Id:t läses ur `metadata.projectId` FÖRST. PU-notiserna skickar
 * doc.procurementId till createNotification, men `project` på notisen är en ref
 * till Project — en procurementId matchar ingen Project och fältet landar som
 * tomt. Mätt på de tre senaste puAccepted i produktion: `project` är tom i alla
 * tre, medan `metadata.projectId` bär upphandlingens id.
 */
const kalkylIntern = ({ projectId, metadata }) => {
  const id = metadata?.projectId || projectId;
  return id ? `/kalkyl/${id}` : null;
};

// Estimatvyn identifieras av Lead-id, inte projekt-id. För estimatnotiser
// ligger det i notification.projectId; metadata.leadId är reserv.
const estimatKund = ({ leadId }) => (leadId ? `/estimat/view/${leadId}` : null);
const estimatIntern = ({ leadId }) => (leadId ? `/estimat/${leadId}` : null);

// Första byggaren som ger en sökväg vinner. Används där den exakta vyn kräver
// ett id som kan saknas — notiser skapade innan fältet fanns i schemat har det
// inte, och då är en fungerande översikt bättre än en trasig djuplänk.
const eller = (...byggare) => (ctx) => {
  for (const b of byggare) {
    const path = typeof b === 'function' ? b(ctx) : b;
    if (path) return path;
  }
  return null;
};

// Offertvyerna kräver offertId. Båda rutterna renderar samma komponent.
// Kunden landar på sin startsida med offertmodalen öppen — samma vy som under
// Projektdokument — i stället för på en fristående sida utan väg tillbaka.
// MyProjects läser ?offert= och öppnar modalen.
const offertKund = ({ metadata }) => (metadata.offertId ? `/myprojects?offert=${metadata.offertId}` : null);
const offertIntern = ({ metadata }) => (metadata.offertId ? `/offert-page/${metadata.offertId}` : null);

// Brief-notiser täcker tre olika saker. metadata.source finns redan i schemat
// och skiljer dem åt; utan den är granskningsvyn det rimliga målet.
const briefIntern = ({ projectId, metadata }) => {
  if (!projectId) return null;
  if (metadata.source === 'todo-create' || metadata.source === 'todo-complete') {
    return `${WS}/projektering/${projectId}/attgora`;
  }
  if (metadata.source === 'kanban-task') return `${WS}/projektering/${projectId}/kanban`;
  return `${WS}/projektering/${projectId}/brief`;
};

// Taggning sker i två vyer och metadata.source säger vilken
const omnamnande = (prefix) => ({ projectId, metadata }) => {
  if (!projectId) return null;
  const kund = prefix === KUND;
  const rumsa = kund ? 'rumsbeskrivning' : 'rumsa';
  const flik = metadata.source === 'rumsbeskrivning' ? rumsa : 'produktval';
  return kund ? `${KUND}/${projectId}/${flik}` : `${WS}/projektering/${projectId}/${flik}`;
};

// ── Tabellen ───────────────────────────────────────────────────────────────
// null i en gren = rollen har ingen egen vy för detta, fall tillbaka.

const TABELL = {
  // IA-tillägg — kunden svarar under Ritningar, IA hanterar dem i Att göra
  ia_tillagg_sent: { kund: kundSida('ritningar'), intern: projektering('attgora') },
  ia_tillagg_accepted: { kund: kundSida('ritningar'), intern: projektering('attgora') },
  ia_tillagg_declined: { kund: kundSida('ritningar'), intern: projektering('attgora') },
  ia_tillagg_message: { kund: kundSida('ritningar'), intern: projektering('attgora') },
  ia_tillagg_klar: { kund: kundSida('ritningar'), intern: projektering('attgora') },

  // ÄTA
  ata_sent: { kund: kundSida('ata'), intern: motenEllerAta('ata') },
  ata_message: { kund: kundSida('ata'), intern: motenEllerAta('ata') },
  ata_accepted: { kund: kundSida('ata'), intern: motenEllerAta('ata') },
  ata_declined: { kund: kundSida('ata'), intern: motenEllerAta('ata') },
  ata_status_changed: { kund: kundSida('ata'), intern: motenEllerAta('ata') },

  // Brief, kanban, todo — alla skapas med type 'brief'
  brief: { kund: null, intern: briefIntern },

  // Estimat
  estimatSent: { kund: estimatKund, intern: estimatIntern },
  estimatUpdated: { kund: estimatKund, intern: estimatIntern },
  estimatApproved: { kund: estimatKund, intern: estimatIntern },
  estimatDeclined: { kund: estimatKund, intern: estimatIntern },
  estimatApprovedByConsultant: { kund: estimatKund, intern: estimatIntern },
  estimatFeedback: { kund: estimatKund, intern: estimatIntern },
  estimatMeetingProposal: { kund: estimatKund, intern: estimatIntern },
  estimatConsultantResponse: { kund: estimatKund, intern: estimatIntern },
  time_proposal_reply: { kund: estimatKund, intern: estimatIntern },
  timeProposalAccepted: { kund: estimatKund, intern: estimatIntern },
  estimat_followup_booked: { kund: estimatKund, intern: estimatIntern },

  // Kundinfo
  kundinfoKlar: { kund: kundSida('kundinfo'), intern: projektering('kundinfo') },

  // Möten
  meeting: { kund: null, intern: motenEllerAta('moten') },
  meeting_proposal: { kund: null, intern: motenEllerAta('moten') },
  meeting_response: { kund: null, intern: motenEllerAta('moten') },
  meeting_booked: { kund: null, intern: motenEllerAta('moten') },
  meeting_cancelled: { kund: null, intern: motenEllerAta('moten') },
  meeting_rescheduled: { kund: null, intern: motenEllerAta('moten') },
  homework_uploaded: { kund: null, intern: motenEllerAta('moten') },
  // Kunden har lagt en fil i den delade Underlag-mappen. Backend skickar med
  // metadata.link, men raden finns så typen fungerar även utan den.
  customer_file_uploaded: { kund: null, intern: projektering('underlag') },
  protocol_published: { kund: null, intern: protokollPaMotet },
  // Handlar om ritningsunderlag trots namnet — inte om ett möte i produktionen
  meeting_request_ia: { kund: kundSida('ritningar'), intern: projektering('attgora') },

  // Offert
  offert_received: { kund: eller(offertKund, '/myprojects'), intern: eller(offertIntern, `${WS}/upphandling`) },
  offert_response: { kund: eller(offertKund, '/myprojects'), intern: eller(offertIntern, `${WS}/upphandling`) },
  offert_customer_reply: { kund: eller(offertKund, '/myprojects'), intern: eller(offertIntern, `${WS}/upphandling`) },
  offertConsultantResponse: { kund: eller(offertKund, '/myprojects'), intern: eller(offertIntern, `${WS}/upphandling`) },
  offertAcceptedByConsultant: { kund: eller(offertKund, '/myprojects'), intern: eller(offertIntern, `${WS}/upphandling`) },

  // Upphandling
  procurementStatusChanged: { kund: null, intern: fast(`${WS}/upphandling`) },
  homeVisitProposalsBooked: { kund: null, intern: fast(`${WS}/upphandling`) },
  homeVisitProposalsProposalAccepted: { kund: null, intern: fast(`${WS}/upphandling`) },
  customerHomeVisitProposals: { kund: null, intern: fast(`${WS}/upphandling`) },
  home_visit_proposal_reply: { kund: null, intern: fast(`${WS}/upphandling`) },
  // Uppföljningsmötet går åt båda hållen — byggkonsulten föreslår tider som
  // kunden svarar på, och kunden föreslår tider som byggkonsulten svarar på.
  // Därför behöver varje typ ett mål även för kunden; med null blev notisen
  // oklickbar för den som skulle agera på den.
  offertFollowupRequested: { kund: fast('/myprojects'), intern: fast(`${WS}/upphandling`) },
  offert_followup_new_proposal: { kund: fast('/myprojects'), intern: fast(`${WS}/upphandling`) },
  offert_followup_accepted: { kund: fast('/myprojects'), intern: fast(`${WS}/upphandling`) },
  offert_followup_declined: { kund: fast('/myprojects'), intern: fast(`${WS}/upphandling`) },
  offert_followup_booked: { kund: fast('/myprojects'), intern: fast(`${WS}/upphandling`) },
  puStarted: { kund: null, intern: fast(`${WS}/upphandling`) },
  offerSent: { kund: null, intern: fast(`${WS}/upphandling`) },
  projectStarted: { kund: null, intern: fast(`${WS}/upphandling`) },
  projectCreated: { kund: fast('/myprojects'), intern: projektering('kanban') },

  // PU
  /**
   * Byggarens svar på ett PU går till BYGGKONSULTEN, inte till en byggare.
   *
   * De här pekade på /workspace/mina-pu, som renderar byggarens ModernPU och
   * läser /api/pu/my — spärrad till rollen byggare. En byggkonsult som klickade
   * på "C&M har godkänt PU" landade därför i en vy som svarade 403 och visade
   * ingenting.
   *
   * Kalkylen är rätt mål: det är där PU-listan, versionerna och byggarens
   * korrigeringar finns. Notisens `project` bär procurementId
   * (puRoutes skickar doc.procurementId till createNotification), vilket är
   * precis vad /kalkyl/:procurementId väntar sig.
   */
  puAccepted: { kund: null, intern: kalkylIntern },
  puDeclined: { kund: null, intern: kalkylIntern },
  puCommented: { kund: null, intern: kalkylIntern },
  puSuggestionReceived: { kund: null, intern: kalkylIntern },

  // Underleverantörer (el, VVS). Backend skickar metadata.link, som vinner över
  // den här tabellen — raderna finns för att notisen inte ska bli oklickbar om
  // länken någon gång saknas. UE landar på översikten, där kortet ligger:
  // flikraden är dold för rollen, så /mina-pu går inte att nå den vägen.
  pu_delad_till_ue: { kund: null, intern: fast(`${WS}/oversikt`) },
  ue_svarade_pu: { kund: null, intern: fast(`${WS}/mina-pu`) },

  // Ritningar
  drawing_review: { kund: kundSida('ritningar'), intern: projektering('attgora') },
  drawing_reply: { kund: kundSida('ritningar'), intern: projektering('attgora') },
  drawing_delivery: { kund: kundSida('ritningar'), intern: projektering('attgora') },

  // Rumsbeskrivning — de två sista matchade inte substrängen 'rumsa' och föll
  // därför till kanban
  rumsaUploaded: { kund: kundSida('rumsbeskrivning'), intern: projektering('rumsa') },
  rumsaUpdated: { kund: kundSida('rumsbeskrivning'), intern: projektering('rumsa') },
  rumsaDeleted: { kund: kundSida('rumsbeskrivning'), intern: projektering('rumsa') },
  rumsbeskrivning_updated: { kund: kundSida('rumsbeskrivning'), intern: projektering('rumsa') },
  rumsbeskrivning_deleted: { kund: kundSida('rumsbeskrivning'), intern: projektering('rumsa') },

  // Publicering för kund — notis + mail går ut när underlaget blir tillgängligt
  timeline_published: { kund: kundSida('timeline'), intern: projektering('tidplan') },
  shoppingList_published: { kund: kundSida('produktval'), intern: projektering('produktval') },
  rumsa_published: { kund: kundSida('rumsbeskrivning'), intern: projektering('rumsa') },

  // Produktval
  shoppingListUpdated: { kund: kundSida('produktval'), intern: projektering('produktval') },
  shoppingListDeleted: { kund: kundSida('produktval'), intern: projektering('produktval') },
  shoppingListUploaded: { kund: kundSida('produktval'), intern: projektering('produktval') },
  commentMention: { kund: omnamnande(KUND), intern: omnamnande(WS) },

  // Projekt
  projectTypeUpdated: { kund: fast('/myprojects'), intern: fast(`${WS}/projekt`) },
  projectDatesUpdated: { kund: kundSida('timeline'), intern: projektering('tidplan') },
  projectUpdated: { kund: fast('/myprojects'), intern: fast(`${WS}/projekt`) },
  projectRolesUpdated: { kund: fast('/myprojects'), intern: fast(`${WS}/projekt`) },
  dokumentationDelivered: { kund: kundSida('dokumentation'), intern: projektering('dokumentation') },

  // Tidplan
  timelineDeleted: { kund: kundSida('timeline'), intern: projektering('tidplan') },
  timelineUploaded: { kund: kundSida('timeline'), intern: projektering('tidplan') },
  timelineUpdated: { kund: kundSida('timeline'), intern: projektering('tidplan') },
  timelineProposalSubmitted: { kund: kundSida('timeline'), intern: projektering('tidplan') },
  timelineProposalResponded: { kund: kundSida('timeline'), intern: projektering('tidplan') },
  timeline_reminder: { kund: kundSida('timeline'), intern: projektering('tidplan') },

  // Uppdateringsflöden
  'project-update-mention': { kund: null, intern: fast(`${WS}/projekt`) },
  'procurement-update-mention': { kund: null, intern: fast(`${WS}/upphandling`) },

  // Leverantör — ingen dedikerad vy finns
  supplier_contact_request: { kund: null, intern: projektering('kanban') }
};

/**
 * Sista utvägen när typen saknar mappning.
 *
 * Landar på rollens FÖRSTA tillåtna flik i stället för hårdkodad 'kanban'.
 * UE har inte kanban och projektör har inga projekteringsflikar alls, så den
 * gamla defaulten skickade dem till sidor de saknade behörighet till.
 */
const fallback = ({ projectId, role }) => {
  // Kunder går till projektlistan, inte till en gissad flik. projectId kan vara
  // ett Lead- eller Procurement-id beroende på notistyp, och då blir
  // /myproject/<det id:t>/... en trasig länk. Projektlistan fungerar alltid.
  if (role === 'kund') return '/myprojects';
  if (!projectId) return null;
  const flik = getDefaultSubTab(role, SECTIONS.PROJEKTERING);
  if (flik) return `${WS}/projektering/${projectId}/${flik}`;
  const produktionsflik = getDefaultSubTab(role, SECTIONS.PRODUKTION);
  if (produktionsflik) return `${WS}/produktion/${projectId}/${produktionsflik}`;
  return `${WS}/oversikt`;
};

/**
 * Bygger länken för en notis, eller null om ingen vettig finns.
 *
 * @param {Object} notification - notisen som den kommer från API:et
 * @param {Object} user - inloggad användare (behöver role)
 * @returns {string|null}
 */
export const byggNotisLank = (notification, user) => {
  if (!notification) return null;
  const role = user?.role;
  const metadata = notification.metadata || {};

  // Backend kan skicka en färdig sökväg när den vet bättre än tabellen
  if (metadata.link) return metadata.link;

  // projectId kan vara populerat objekt eller rå-id, och bär beroende på
  // notistyp ett Project-, Lead- eller Procurement-id
  const rawId = typeof notification.projectId === 'object'
    ? notification.projectId?._id
    : notification.projectId;
  const projectId = rawId ? String(rawId) : null;

  const ctx = {
    projectId,
    leadId: metadata.leadId ? String(metadata.leadId) : projectId,
    metadata,
    role
  };

  const post = TABELL[notification.type];

  // Typen finns i tabellen men rollen har ingen egen vy för den — t.ex. en
  // mötesnotis till kund, som saknar mötessida. Då ska notisen vara oklickbar
  // i stället för att skicka användaren till något orelaterat.
  if (post && !post[role === 'kund' ? 'kund' : 'intern']) return null;

  const gren = role === 'kund' ? post?.kund : post?.intern;
  const path = typeof gren === 'function' ? gren(ctx) : null;

  if (path) {
    // Skicka aldrig en intern roll till en flik den saknar behörighet till
    const match = path.match(/^\/workspace\/(projektering|produktion)\/[^/]+\/([^/?]+)/);
    if (match && !canAccessSubTab(role, match[1], match[2])) {
      return fallback(ctx);
    }
    return path;
  }

  return fallback(ctx);
};

export default byggNotisLank;
